import { useEffect, useState } from 'react';
import { createItem, deleteItem, fetchList, fetchLookups, updateItem } from './api';
import { canWriteEmpresaData } from '../../auth/session';
import CrudModal from '../../components/layout/CrudModal';
import { formatCurrencyBR, normalizeCurrencyInputBR, parseCurrencyBR } from '../../utils/numberBR';

const EMPTY = {
  obra_id: '',
  conta_id: '',
  centro_custo_id: '',
  competencia: '',
  valor_orcado: '',
};

const ConstrutorOrcamentosPage = () => {
  const canWrite = canWriteEmpresaData();
  const [items, setItems] = useState([]);
  const [lookups, setLookups] = useState({ obras: [], contas: [], centros: [] });
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState('');
  const [showFormModal, setShowFormModal] = useState(false);
  const [obraFilter, setObraFilter] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const load = async () => {
    const [list, lk] = await Promise.all([fetchList('/api/construtor/orcamentos', { obra_id: obraFilter, page, pageSize: 10 }), fetchLookups()]);
    setItems(Array.isArray(list?.items) ? list.items : []);
    setTotal(Number(list?.total || 0));
    setLookups(lk || { obras: [], contas: [], centros: [] });
  };

  useEffect(() => {
    load();
  }, [page]);

  const onSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      centro_custo_id: form.centro_custo_id || null,
      valor_orcado: parseCurrencyBR(form.valor_orcado) ?? 0,
    };
    if (editingId) {
      await updateItem('/api/construtor/orcamentos', editingId, payload);
    } else {
      await createItem('/api/construtor/orcamentos', payload);
    }
    setEditingId('');
    setForm(EMPTY);
    setShowFormModal(false);
    await load();
  };

  const onDelete = async (id) => {
    if (!window.confirm('Excluir orcamento?')) return;
    await deleteItem('/api/construtor/orcamentos', id);
    await load();
  };

  const onEdit = (item) => {
    setEditingId(item.id);
    setForm({
      obra_id: item.obra_id || '',
      conta_id: item.conta_id || '',
      centro_custo_id: item.centro_custo_id || '',
      competencia: item.competencia || '',
      valor_orcado: item.valor_orcado == null ? '' : formatCurrencyBR(item.valor_orcado),
    });
    setShowFormModal(true);
  };

  const totalPages = Math.max(1, Math.ceil(total / 10));

  return (
    <div className="animate-fade-in space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Orcamentos</h1>
        {canWrite && (
          <button
            type="button"
            className="btn-primary"
            onClick={() => {
              setEditingId('');
              setForm(EMPTY);
              setShowFormModal(true);
            }}
          >
            Incluir Orcamento
          </button>
        )}
      </div>

      {canWrite && (
        <CrudModal
          open={showFormModal}
          title={editingId ? 'Editar Orcamento' : 'Novo Orcamento'}
          onClose={() => {
            setShowFormModal(false);
            setEditingId('');
            setForm(EMPTY);
          }}
          maxWidthClass="max-w-5xl"
        >
          <form onSubmit={onSubmit} className="space-y-5">
            <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-4">
              <p className="text-sm font-semibold text-gray-800">Composicao do Orcamento</p>
              <p className="text-xs text-gray-500 mt-1">Vincule obra, conta contabil e competencia com o valor previsto.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div>
                <label className="form-label">Obra</label>
                <select className="form-select" value={form.obra_id} onChange={(e) => setForm({ ...form, obra_id: e.target.value })}>
                  <option value="">Selecione</option>
                  {lookups.obras.map((o) => <option key={o.id} value={o.id}>{o.nome}</option>)}
                </select>
              </div>
              <div>
                <label className="form-label">Conta Contabil</label>
                <select className="form-select" value={form.conta_id} onChange={(e) => setForm({ ...form, conta_id: e.target.value })}>
                  <option value="">Selecione</option>
                  {lookups.contas.map((c) => <option key={c.id} value={c.id}>{c.codigo} - {c.descricao}</option>)}
                </select>
              </div>
              <div>
                <label className="form-label">Centro de Custo</label>
                <select className="form-select" value={form.centro_custo_id} onChange={(e) => setForm({ ...form, centro_custo_id: e.target.value })}>
                  <option value="">Opcional</option>
                  {lookups.centros.map((c) => <option key={c.id} value={c.id}>{c.codigo} - {c.descricao}</option>)}
                </select>
              </div>
              <div>
                <label className="form-label">Competencia</label>
                <input className="form-input" type="month" value={form.competencia} onChange={(e) => setForm({ ...form, competencia: e.target.value })} />
              </div>
              <div>
                <label className="form-label">Valor Orcado (R$)</label>
                <input
                  className="form-input"
                  inputMode="decimal"
                  placeholder="0,00"
                  value={form.valor_orcado}
                  onChange={(e) => setForm({ ...form, valor_orcado: normalizeCurrencyInputBR(e.target.value) })}
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 border-t pt-4">
              <button
                className="btn-secondary"
                type="button"
                onClick={() => {
                  setShowFormModal(false);
                  setEditingId('');
                  setForm(EMPTY);
                }}
              >
                Cancelar
              </button>
              <button className="btn-primary" type="submit">{editingId ? 'Atualizar Orcamento' : 'Salvar Orcamento'}</button>
            </div>
          </form>
        </CrudModal>
      )}

      <div className="card">
        <div className="card-header flex items-center gap-3">
          <select className="form-select max-w-sm" value={obraFilter} onChange={(e) => setObraFilter(e.target.value)}>
            <option value="">Todas obras</option>
            {lookups.obras.map((o) => <option key={o.id} value={o.id}>{o.nome}</option>)}
          </select>
          <button className="btn-secondary" onClick={() => { setPage(1); load(); }}>Filtrar</button>
        </div>
        <div className="card-body p-0">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Obra</th>
                  <th>Conta</th>
                  <th>Competencia</th>
                  <th>Valor</th>
                  <th>Acoes</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id}>
                    <td>{it.obra_nome}</td>
                    <td>{it.conta_codigo} - {it.conta_descricao}</td>
                    <td>{it.competencia}</td>
                    <td>R$ {Number(it.valor_orcado || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td>
                      {canWrite && <button className="btn-secondary mr-2" onClick={() => onEdit(it)}>Editar</button>}
                      {canWrite && <button className="btn-danger" onClick={() => onDelete(it.id)}>Excluir</button>}
                    </td>
                  </tr>
                ))}
                {items.length === 0 && <tr><td colSpan={5} className="text-center text-gray-500 py-6">Sem dados</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
        <div className="card-body border-t flex items-center justify-between">
          <span className="text-sm text-gray-600">Pagina {page} de {totalPages} ({total} registros)</span>
          <div className="flex gap-2">
            <button className="btn-secondary" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Anterior</button>
            <button className="btn-secondary" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Proxima</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConstrutorOrcamentosPage;
